import { FC, useEffect, useMemo, useState } from "react";
import { Stack, styled, Typography } from "@mui/material";
import { TopPage } from "@/components/elements/TopPage";
import { InnerBox } from "@/components/elements/InnerBox";
import { Button } from "@/components/elements/Button";
import SaveIcon from "@mui/icons-material/Save";
import { AppRoutes } from "@/routes/config";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "@/utils/toast";
import { API_SUCCESS_MESSAGE, API_UNKNOWN_ERROR_MESSAGE } from "@/constants/common";
import { importJobsApi } from "@/api/services/main/job";
import { FooterContainer } from "@/components/commons/FooterContainer";
import { JobAfterImport } from "@/features/job/components/job-import/JobAfterImport";
import { divider } from "@/theme/colors";
import { Form } from "@/components/elements/Form";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { validator } from "@/utils/validator";
import { TApiError, TApiErrorField } from "@/types/api";
import { LoadingArea } from "@/components/elements/Loading/Loading";
import { ConfirmSaveModal } from "@/components/commons/ConfirmSaveModal";

type TForm = {
  hasValue: boolean;
};

const formSchema = validator.object().shape({
  hasValue: validator.boolean().required(),
});

export const JobImportPage: FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { jobs } = location.state || {};
  const totalRow = jobs?.length ?? 0;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showNewCompanyConfirm, setShowNewCompanyConfirm] = useState(false);

  // Collect unique new company names from validated import data
  const newCompanyNames = useMemo(() => {
    if (!jobs) return [];
    const names = new Set<string>();
    for (const job of jobs) {
      if (job.newCompanyName) names.add(job.newCompanyName);
    }
    return Array.from(names);
  }, [jobs]);

  const methods = useForm<TForm>({
    defaultValues: { hasValue: false },
    mode: "all",
    reValidateMode: "onBlur",
    resolver: yupResolver(formSchema),
  });

  useEffect(() => {
    if (!jobs) navigate(AppRoutes.jobList);
    methods.setValue("hasValue", true, { shouldDirty: true });
  });

  const handleSaveClick = () => {
    if (jobs.length === 0) return;
    // Show confirmation modal if new companies will be created
    if (newCompanyNames.length > 0) {
      setShowNewCompanyConfirm(true);
      return;
    }
    handleSaveData();
  };

  const handleSaveData = async () => {
    setShowNewCompanyConfirm(false);
    if (jobs.length === 0) return;
    setIsSubmitting(true);
    try {
      await importJobsApi(jobs);
      toast.success(API_SUCCESS_MESSAGE);
      navigate(AppRoutes.jobList);
    } catch (err) {
      let message: string;
      const error = err as TApiError;

      if (Array.isArray(error.error)) {
        message = error.error.map((e: TApiErrorField) => `${e.property}: ${e.message}`).join("\n");
      } else {
        message = error.error || error.message || API_UNKNOWN_ERROR_MESSAGE;
      }

      toast.error(message);
    }
    setIsSubmitting(false);
  };

  const TitleInnerBox: FC = () => {
    return (
      <Typography variant="sub16Semi">
        ファイルアップロード結果：<Typography variant="head20Semi">{totalRow}</Typography> 件
      </Typography>
    );
  };

  return (
    <>
      <PageWrapper>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
          <TopPage title="複数求人登録" enablePageBack={false} />
        </Stack>
        <Content>
          <InnerBox borderColor={divider.high} padding={3} mt={2} border="none" showDivider={false} title={<TitleInnerBox />}>
            {!jobs ? (
              <LoadingArea height={300} />
            ) : (
              <Stack flex={1} gap={3}>
                <JobAfterImport rows={jobs ?? []} isLoading={false} />
              </Stack>
            )}
          </InnerBox>
        </Content>

        <Form methods={methods} onSubmit={() => {}} schema={formSchema} enableBlocker={!isSubmitting} customMessageLeave="インポートが終了しておりませんが戻ってもよろしいですか？">
          <FooterContainer>
            <Stack gap={1} direction="row" justifyContent="space-between" alignItems="center">
              <Button size="sm" variant="ghost" onClick={() => navigate(AppRoutes.jobList)}>
                戻る
              </Button>
              <Stack direction="row" gap={3}>
                <Button size="sm" variant="primary" disabled={isSubmitting} onClick={handleSaveClick} startIcon={<SaveIcon />}>
                  一括保存
                </Button>
              </Stack>
            </Stack>
          </FooterContainer>
        </Form>
      </PageWrapper>

      <ConfirmSaveModal
        open={showNewCompanyConfirm}
        onClose={() => setShowNewCompanyConfirm(false)}
        onOk={handleSaveData}
        title="企業新規登録の確認"
        enableFooter
      >
        <Typography variant="body14">
          以下の企業（{newCompanyNames.length}社）が新規登録されますが、よろしいでしょうか？
        </Typography>
        <Stack component="ul" sx={{ mt: 1, pl: 2 }}>
          {newCompanyNames.map((name) => (
            <li key={name}>
              <Typography variant="body14">{name}</Typography>
            </li>
          ))}
        </Stack>
      </ConfirmSaveModal>
    </>
  );
};

const PageWrapper = styled(Stack)`
  min-height: calc(100vh - 100px);
  display: flex;
  flex-direction: column;
`;

const Content = styled(Stack)`
  flex: 1;
`;
